import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';
import { RefreshCw, Package } from 'lucide-react';

const formatNumber = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString();
};

const formatShortNumber = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = abs < 10_000_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}M`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = abs < 10_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}K`;
  }
  return Math.round(n).toString();
};

const formatMonthLabel = (ymd) => {
  try {
    const d = new Date(ymd);
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return ymd;
  }
};

const metricField = (m) => m === 'revenue' ? 'revenue' : m === 'orders' ? 'orders_count' : 'items_count';
const metricLabel = (m) => m === 'revenue' ? 'Ciro' : m === 'orders' ? 'Sipariş' : 'Adet';
const fmtCell = (val, m) => {
  if (m === 'revenue') return `₺${formatShortNumber(val)}`;
  return formatShortNumber(val);
};

const formatDayShort = (ymd) => {
  try {
    const d = new Date(ymd);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}`;
  } catch {
    return ymd;
  }
};

export default function IkasProductPerformance() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 29); // default 30 days
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('revenue'); // revenue | orders | items
  const [selectedRange, setSelectedRange] = useState('30d'); // default selected quick range

  const [dailyRows, setDailyRows] = useState([]);
  const [topRows, setTopRows] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [categoryMonthlyRows, setCategoryMonthlyRows] = useState([]);
  const [categoryDailyRows, setCategoryDailyRows] = useState([]);
  const [selectedSectionTab, setSelectedSectionTab] = useState('brand');
  const [salesChannels, setSalesChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [productBrandFilter, setProductBrandFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productDailyPage, setProductDailyPage] = useState(1);
  const [productMonthlyPage, setProductMonthlyPage] = useState(1);
  const PRODUCTS_PER_PAGE = 50;

  // Aggregate chart data from dailyRows (sum of all products per day)
  const lineData = useMemo(() => {
    const byDate = new Map();
    for (const r of dailyRows) {
      const d = String(r.day).slice(0, 10);
      if (!byDate.has(d)) byDate.set(d, { revenue: 0, orders: 0, items: 0 });
      const acc = byDate.get(d);
      acc.revenue += Number(r.revenue || 0);
      acc.orders += Number(r.orders_count || 0);
      acc.items += Number(r.items_count || 0);
    }
    return Array.from(byDate.entries())
      .sort((a,b) => a[0].localeCompare(b[0]))
      .map(([label, v]) => ({ label, value: metric === 'revenue' ? v.revenue : metric === 'orders' ? v.orders : v.items }));
  }, [dailyRows, metric]);

  const totals = useMemo(() => {
    let revenue = 0, orders = 0, items = 0;
    for (const r of dailyRows) {
      revenue += Number(r.revenue || 0);
      orders  += Number(r.orders_count || 0);
      items   += Number(r.items_count || 0);
    }
    return { revenue, orders, items };
  }, [dailyRows]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      // Demo fallback: synthesize product and category performance for the demo brand
      const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      if (brandId === DEMO_BRAND_ID) {
        // Build date range
        const toDate = (s) => new Date(`${s}T00:00:00`);
        const s = startDate ? toDate(startDate) : new Date(new Date().setDate(new Date().getDate() - 29));
        const e = endDate ? toDate(endDate) : new Date();
        const days = [];
        for (let d = new Date(s); d <= e; d = new Date(d.getTime() + 86400000)) {
          days.push(d.toISOString().slice(0, 10));
        }
        // Daily rows (sum across products)
        const daily = days.map((d, i) => ({
          day: d,
          revenue: 50000 + Math.round(15000 * Math.sin(i / 3)) + (i % 5) * 1800,
          orders_count: 280 + (i % 7) * 8 + Math.round(20 * Math.cos(i / 4)),
          items_count: 620 + (i % 6) * 12 + Math.round(30 * Math.sin(i / 5))
        }));
        setDailyRows(daily);
        // Top products (aggregate)
        const top = [
          { product_id: 'P-1001', sku: 'SKU-1001', brand_name: 'Alpha', product_name: 'Sneaker X', revenue: 1250000, orders_count: 8200, items_count: 15400 },
          { product_id: 'P-1002', sku: 'SKU-1002', brand_name: 'Alpha', product_name: 'T‑Shirt Classic', revenue: 980000, orders_count: 6200, items_count: 12100 },
          { product_id: 'P-1003', sku: 'SKU-1003', brand_name: 'Bravo', product_name: 'Jeans Slim', revenue: 910000, orders_count: 5900, items_count: 11200 },
          { product_id: 'P-1004', sku: 'SKU-1004', brand_name: 'Bravo', product_name: 'Hoodie Warm', revenue: 860000, orders_count: 5400, items_count: 10300 },
          { product_id: 'P-1005', sku: 'SKU-1005', brand_name: 'Charlie', product_name: 'Backpack Pro', revenue: 740000, orders_count: 4800, items_count: 9200 }
        ];
        setTopRows(top);
        // Monthly rows (per product)
        const now = new Date();
        const months = Array.from({ length: 6 }, (_, k) => {
          const d = new Date(now.getFullYear(), now.getMonth() - (5 - k), 1);
          return d.toISOString().slice(0, 10);
        });
        const monthly = [];
        const prods = [
          { id: 'P-1001', name: 'Sneaker X', sku: 'SKU-1001', brand: 'Alpha' },
          { id: 'P-1002', name: 'T‑Shirt Classic', sku: 'SKU-1002', brand: 'Alpha' },
          { id: 'P-1003', name: 'Jeans Slim', sku: 'SKU-1003', brand: 'Bravo' },
          { id: 'P-1004', name: 'Hoodie Warm', sku: 'SKU-1004', brand: 'Bravo' },
          { id: 'P-1005', name: 'Backpack Pro', sku: 'SKU-1005', brand: 'Charlie' }
        ];
        for (let pIdx = 0; pIdx < prods.length; pIdx++) {
          for (let mIdx = 0; mIdx < months.length; mIdx++) {
            const base = 120000 - pIdx * 9000 + mIdx * 6000;
            monthly.push({
              month: months[mIdx],
              product_id: prods[pIdx].id,
              sku: prods[pIdx].sku,
              brand_name: prods[pIdx].brand,
              product_name: prods[pIdx].name,
              revenue: Math.max(20000, base),
              orders_count: 800 - pIdx * 40 + mIdx * 25,
              items_count: 1600 - pIdx * 70 + mIdx * 40
            });
          }
        }
        setMonthlyRows(monthly);
        // Category monthly
        const catMonthly = [];
        const cats = ['Giyim', 'Ayakkabı', 'Aksesuar'];
        for (let ci = 0; ci < cats.length; ci++) {
          for (let mIdx = 0; mIdx < months.length; mIdx++) {
            const base = 300000 - ci * 25000 + mIdx * 15000;
            catMonthly.push({
              month: months[mIdx],
              category_name: cats[ci],
              revenue: Math.max(50000, base)
            });
          }
        }
        setCategoryMonthlyRows(catMonthly);
        // Category daily (selected range)
        const catDaily = [];
        for (let i = 0; i < days.length; i++) {
          catDaily.push({ day: days[i], category_name: 'Giyim', revenue: 22000 + (i % 5) * 1600 });
          catDaily.push({ day: days[i], category_name: 'Ayakkabı', revenue: 18000 + (i % 7) * 1400 });
          catDaily.push({ day: days[i], category_name: 'Aksesuar', revenue: 9000 + (i % 4) * 900 });
        }
        setCategoryDailyRows(catDaily);
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (selectedChannel) params.set('salesChannel', selectedChannel);
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      // Fetch sales channels for dropdown
      try {
        const chRes = await fetch(`${base}/api/ikas/sales-channels`, { credentials: 'include', headers });
        const chJs = await chRes.json().catch(() => ({}));
        if (chRes.ok && chJs?.channels) setSalesChannels(chJs.channels);
      } catch {}
      const res = await fetch(`${base}/api/ikas/products/daily?${params.toString()}`, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const rows = res.ok && js?.rows ? js.rows : [];
      setDailyRows(rows);
      // Aggregate per product for top table
      const byProduct = new Map();
      for (const r of rows) {
        const key = `${r.product_id || ''}__${r.sku || ''}`;
        if (!byProduct.has(key)) {
          byProduct.set(key, {
            product_id: r.product_id,
            sku: r.sku,
            brand_name: r.brand_name,
            product_name: r.product_name,
            revenue: 0, orders_count: 0, items_count: 0
          });
        }
        const acc = byProduct.get(key);
        acc.revenue += Number(r.revenue || 0);
        acc.orders_count += Number(r.orders_count || 0);
        acc.items_count += Number(r.items_count || 0);
      }
      setTopRows(Array.from(byProduct.values()).sort((a,b) => b.revenue - a.revenue).slice(0, 25));
      // Monthly brands (aggregate in table) - always all-time (unaffected by date filter)
      const monthlyParams = new URLSearchParams();
      if (selectedChannel) monthlyParams.set('salesChannel', selectedChannel);
      const resM = await fetch(`${base}/api/ikas/products/monthly?${monthlyParams.toString()}`, { credentials: 'include', headers });
      const jsM = await resM.json().catch(() => ({}));
      const rowsM = resM.ok && jsM?.rows ? jsM.rows : [];
      setMonthlyRows(rowsM);
      // Category monthly (all-time, but channel-filtered if a channel is selected)
      try {
        const catMParams = new URLSearchParams();
        if (selectedChannel) catMParams.set('salesChannel', selectedChannel);
        const resCatM = await fetch(`${base}/api/ikas/product-category/monthly?${catMParams.toString()}`, { credentials: 'include', headers });
        const jsCatM = await resCatM.json().catch(() => ({}));
        const rowsCatM = resCatM.ok && jsCatM?.rows ? jsCatM.rows : [];
        setCategoryMonthlyRows(rowsCatM);
      } catch {}
      // Category daily (selected range)
      try {
        const resCatD = await fetch(`${base}/api/ikas/product-category/daily?${params.toString()}`, { credentials: 'include', headers });
        const jsCatD = await resCatD.json().catch(() => ({}));
        const rowsCatD = resCatD.ok && jsCatD?.rows ? jsCatD.rows : [];
        setCategoryDailyRows(rowsCatD);
      } catch {}
    } catch (e) {
      console.error('IkasProductPerformance fetch error', e);
      setDailyRows([]);
      setTopRows([]);
      setMonthlyRows([]);
      setCategoryMonthlyRows([]);
      setCategoryDailyRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedChannel]);

  useEffect(() => {
    // Reset product table pages when filters or tab change
    setProductDailyPage(1);
    setProductMonthlyPage(1);
  }, [productBrandFilter, productSearch, selectedSectionTab, metric, startDate, endDate, selectedChannel]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            <img
              src="/ikaslogoicon.png"
              alt="Ikas"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Ürün Performansı</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {[
                { id: '7d', label: '7 Gün' },
                { id: '30d', label: '30 Gün' },
                { id: 'thisMonth', label: 'Bu Ay' },
                { id: 'lastMonth', label: 'Geçen Ay' },
                { id: 'all', label: 'Tüm Zamanlar' },
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    selectedRange === opt.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }`}
                  onClick={() => {
                    const today = new Date();
                    let s = new Date(today);
                    let e = new Date(today);
                    switch (opt.id) {
                      case '7d': s.setDate(today.getDate() - 6); break;
                      case '30d': s.setDate(today.getDate() - 29); break;
                      case 'thisMonth': s = new Date(today.getFullYear(), today.getMonth(), 1); break;
                      case 'lastMonth': {
                        const firstOfThis = new Date(today.getFullYear(), today.getMonth(), 1);
                        e = new Date(firstOfThis.getTime() - 86400000);
                        s = new Date(e.getFullYear(), e.getMonth(), 1);
                        break;
                      }
                      case 'all': s = new Date(today.getFullYear() - 3, 0, 1); break;
                      default: break;
                    }
                    const toYmd = (d) => d.toISOString().slice(0, 10);
                    setStartDate(toYmd(s));
                    setEndDate(toYmd(e));
                    setSelectedRange(opt.id);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {salesChannels.length > 0 && (
              <select
                className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 cursor-pointer"
                value={selectedChannel}
                onChange={(e) => setSelectedChannel(e.target.value)}
              >
                <option value="">Tüm Kanallar</option>
                {salesChannels.map(ch => (
                  <option key={ch} value={ch}>{ch}</option>
                ))}
              </select>
            )}
            <button
              className="p-2 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-60"
              onClick={fetchData}
              disabled={loading}
              title="Yenile"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4">
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Ciro</p>
                <p className="text-lg font-semibold mt-1">₺{formatShortNumber(totals.revenue)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Sipariş</p>
                <p className="text-lg font-semibold mt-1">{formatNumber(totals.orders)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Adet</p>
                <p className="text-lg font-semibold mt-1">{formatNumber(totals.items)}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50">
                <Package className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Daily trend */}
        <div className="flex items-center justify-between mt-5 mb-3">
          <div className="text-sm font-medium text-gray-800">Günlük Trend</div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'revenue' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('revenue')}
            >
              Revenue
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'orders' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('orders')}
            >
              Orders
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'items' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('items')}
            >
              Items
            </button>
          </div>
        </div>
        <div className="h-72">
          <LineChart 
            data={{
              labels: lineData.map(d => d.label),
              datasets: [{
                label: metric === 'revenue' ? 'Ciro' : metric === 'orders' ? 'Sipariş' : 'Adet',
                data: lineData.map(d => d.value),
                borderColor: metric === 'revenue' ? 'rgb(16, 185, 129)' : metric === 'orders' ? 'rgb(37, 99, 235)' : 'rgb(217, 119, 6)',
                backgroundColor: metric === 'revenue' ? 'rgba(16, 185, 129, 0.1)' : (metric === 'orders' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(217, 119, 6, 0.1)'),
                fill: true,
                tension: 0.4
              }]
            }}
          />
        </div>
      </div>

      {/* Section Selector (Brand / Category / Product) + Metric Toggle */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {[
              { id: 'brand', label: 'Marka' },
              { id: 'category', label: 'Kategori' },
              { id: 'product', label: 'Ürün' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedSectionTab === opt.id
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-700 bg-gray-50 hover:bg-white hover:text-gray-900 border border-gray-200'
                }`}
                onClick={() => setSelectedSectionTab(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {[
              { id: 'revenue', label: 'Ciro', color: 'text-emerald-700' },
              { id: 'orders', label: 'Sipariş', color: 'text-blue-700' },
              { id: 'items', label: 'Adet', color: 'text-amber-700' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  metric === opt.id ? `bg-white shadow-sm ${opt.color}` : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setMetric(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedSectionTab === 'brand' && (
      <>
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">En İyi Markalar (Aylık {metricLabel(metric)})</div>
        </div>
        {(() => {
          const field = metricField(metric);
          const monthsSet = new Set((monthlyRows || []).map(r => String(r.month).slice(0, 10)));
          const months = Array.from(monthsSet).sort().reverse();
          const valuesByBrand = new Map();
          const totals = new Map();
          for (const r of (monthlyRows || [])) {
            const m = String(r.month).slice(0, 10);
            const brand = r.brand_name || 'Unknown';
            const val = Number(r[field] || 0);
            if (!valuesByBrand.has(brand)) valuesByBrand.set(brand, {});
            valuesByBrand.get(brand)[m] = (valuesByBrand.get(brand)[m] || 0) + val;
            totals.set(brand, (totals.get(brand) || 0) + val);
          }
          const brands = Array.from(valuesByBrand.keys()).sort((a, b) => (totals.get(b) || 0) - (totals.get(a) || 0));
          if (brands.length === 0) {
            return <div className="text-sm text-gray-500">No data.</div>;
          }
          return (
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Marka</th>
                    {months.map(m => (
                      <th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {brands.map(brand => (
                    <tr key={brand} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{brand}</td>
                      {months.map(m => (
                        <td key={`${brand}-${m}`} className="py-2 pr-4">{fmtCell((valuesByBrand.get(brand)?.[m]) || 0, metric)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Daily Brand Performance (affected by date filter) */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Günlük Marka {metricLabel(metric)}</div>
        </div>
        {(() => {
          const field = metricField(metric);
          const daySet = new Set((dailyRows || []).map(r => String(r.day).slice(0, 10)));
          const days = Array.from(daySet).sort((a, b) => b.localeCompare(a));
          const valuesByBrand = new Map();
          const totals = new Map();
          for (const r of (dailyRows || [])) {
            const d = String(r.day).slice(0, 10);
            const brand = r.brand_name || 'Unknown';
            const val = Number(r[field] || 0);
            if (!valuesByBrand.has(brand)) valuesByBrand.set(brand, {});
            valuesByBrand.get(brand)[d] = (valuesByBrand.get(brand)[d] || 0) + val;
            totals.set(brand, (totals.get(brand) || 0) + val);
          }
          const brands = Array.from(valuesByBrand.keys()).sort((a, b) => (totals.get(b) || 0) - (totals.get(a) || 0));
          if (brands.length === 0 || days.length === 0) {
            return <div className="text-sm text-gray-500">No data.</div>;
          }
          return (
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Marka</th>
                    {days.map(d => {
                      const dt = new Date(d);
                      const label = dt.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
                      return <th key={d} className="py-2 pr-4">{label}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {brands.map(brand => (
                    <tr key={brand} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{brand}</td>
                      {days.map(d => (
                        <td key={`${brand}-${d}`} className="py-2 pr-4">{fmtCell((valuesByBrand.get(brand)?.[d]) || 0, metric)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      </>
      )}

      {selectedSectionTab === 'category' && (
      <>
      {/* Categories — Monthly */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Kategoriler (Aylık {metricLabel(metric)})</div>
        </div>
        {(() => {
          const field = metricField(metric);
          const monthsSet = new Set((categoryMonthlyRows || []).map(r => String(r.month).slice(0,10)));
          const months = Array.from(monthsSet).sort().reverse();
          const byCat = new Map();
          const totals = new Map();
          for (const r of (categoryMonthlyRows || [])) {
            const m = String(r.month).slice(0,10);
            const cat = r.category_name || 'Unknown';
            const val = Number(r[field] || 0);
            if (!byCat.has(cat)) byCat.set(cat, {});
            byCat.get(cat)[m] = (byCat.get(cat)[m] || 0) + val;
            totals.set(cat, (totals.get(cat) || 0) + val);
          }
          const cats = Array.from(byCat.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0)).slice(0,25);
          if (cats.length === 0 || months.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Kategori</th>
                    {months.map(m => (<th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {cats.map(cat => (
                    <tr key={cat} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{cat}</td>
                      {months.map(m => (
                        <td key={`${cat}-${m}`} className="py-2 pr-4">{fmtCell((byCat.get(cat)?.[m]) || 0, metric)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Categories — Daily */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Kategoriler — Günlük {metricLabel(metric)}</div>
        </div>
        {(() => {
          const field = metricField(metric);
          const datesSet = new Set((categoryDailyRows || []).map(r => String(r.day).slice(0,10)));
          const dates = Array.from(datesSet).sort().reverse();
          const byCat = new Map();
          const totals = new Map();
          for (const r of (categoryDailyRows || [])) {
            const d = String(r.day).slice(0,10);
            const cat = r.category_name || 'Unknown';
            const val = Number(r[field] || 0);
            if (!byCat.has(cat)) byCat.set(cat, {});
            byCat.get(cat)[d] = (byCat.get(cat)[d] || 0) + val;
            totals.set(cat, (totals.get(cat) || 0) + val);
          }
          const cats = Array.from(byCat.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0)).slice(0,25);
          if (cats.length === 0 || dates.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Kategori</th>
                    {dates.map(d => (<th key={d} className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">{formatDayShort(d)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {cats.map(cat => (
                    <tr key={cat} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{cat}</td>
                      {dates.map(d => (
                        <td key={`${cat}-${d}`} className="py-2 pr-4">{fmtCell((byCat.get(cat)?.[d]) || 0, metric)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      </>
      )}

      {selectedSectionTab === 'product' && (
      <>
      {/* Product Filters */}
      <div className="bg-white border rounded-2xl p-3 shadow-sm flex flex-wrap items-center gap-3">
        <select
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
          value={productBrandFilter}
          onChange={e => setProductBrandFilter(e.target.value)}
        >
          <option value="">Tüm Markalar</option>
          {(() => {
            const brands = new Set();
            for (const r of (monthlyRows || [])) if (r.brand_name) brands.add(r.brand_name);
            for (const r of (dailyRows || [])) if (r.brand_name) brands.add(r.brand_name);
            return Array.from(brands).sort().map(b => <option key={b} value={b}>{b}</option>);
          })()}
        </select>
        <input
          type="text"
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 w-64"
          placeholder="Ürün adı ara..."
          value={productSearch}
          onChange={e => setProductSearch(e.target.value)}
        />
        {(productBrandFilter || productSearch) && (
          <button
            className="text-xs text-gray-500 hover:text-gray-900 underline"
            onClick={() => { setProductBrandFilter(''); setProductSearch(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Products — Monthly */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">En İyi Ürünler (Aylık {metricLabel(metric)})</div>
        </div>
        {(() => {
          const field = metricField(metric);
          const rows = (monthlyRows || []).filter(r => {
            if (productBrandFilter && r.brand_name !== productBrandFilter) return false;
            if (productSearch && !(r.product_name || '').toLowerCase().includes(productSearch.toLowerCase())) return false;
            return true;
          });
          const monthsSet = new Set(rows.map(r => String(r.month).slice(0,10)));
          const months = Array.from(monthsSet).sort().reverse();
          const byProd = new Map();
          const totals = new Map();
          for (const r of rows) {
            const key = `${r.product_name || r.product_id || '-'}#${r.sku || ''}`;
            const m = String(r.month).slice(0,10);
            const val = Number(r[field] || 0);
            if (!byProd.has(key)) byProd.set(key, {});
            byProd.get(key)[m] = (byProd.get(key)[m] || 0) + val;
            totals.set(key, (totals.get(key) || 0) + val);
          }
          const products = Array.from(byProd.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0));
          const totalPages = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
          const currentPage = Math.min(productMonthlyPage, totalPages);
          const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
          const pagedProducts = products.slice(startIdx, startIdx + PRODUCTS_PER_PAGE);
          if (products.length === 0 || months.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div>
              <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b align-bottom">
                      <th className="py-2 pr-4">Ürün</th>
                      {months.map(m => (<th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedProducts.map(key => {
                      const name = key.split('#')[0];
                      return (
                        <tr key={key} className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-4 font-medium">{name}</td>
                          {months.map(m => (
                            <td key={`${key}-${m}`} className="py-2 pr-4">{fmtCell((byProd.get(key)?.[m]) || 0, metric)}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">
                    Showing {startIdx + 1}-{Math.min(startIdx + PRODUCTS_PER_PAGE, products.length)} of {products.length} products
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-2.5 py-1 text-xs border border-gray-200 rounded-md disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setProductMonthlyPage(p => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span className="text-xs text-gray-600">Sayfa {currentPage} / {totalPages}</span>
                    <button
                      className="px-2.5 py-1 text-xs border border-gray-200 rounded-md disabled:opacity-50"
                      disabled={currentPage >= totalPages}
                      onClick={() => setProductMonthlyPage(p => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Products — Daily */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Ürünler — Günlük {metricLabel(metric)}</div>
        </div>
        {(() => {
          const field = metricField(metric);
          const filtered = (dailyRows || []).filter(r => {
            if (productBrandFilter && r.brand_name !== productBrandFilter) return false;
            if (productSearch && !(r.product_name || '').toLowerCase().includes(productSearch.toLowerCase())) return false;
            return true;
          });
          const datesSet = new Set(filtered.map(r => String(r.day).slice(0,10)));
          const dates = Array.from(datesSet).sort().reverse();
          const byProd = new Map();
          const totals = new Map();
          for (const r of filtered) {
            const d = String(r.day).slice(0,10);
            const key = `${r.product_name || r.product_id || '-'}#${r.sku || ''}`;
            const val = Number(r[field] || 0);
            if (!byProd.has(key)) byProd.set(key, {});
            byProd.get(key)[d] = (byProd.get(key)[d] || 0) + val;
            totals.set(key, (totals.get(key) || 0) + val);
          }
          const products = Array.from(byProd.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0));
          const totalPages = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
          const currentPage = Math.min(productDailyPage, totalPages);
          const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
          const pagedProducts = products.slice(startIdx, startIdx + PRODUCTS_PER_PAGE);
          if (products.length === 0 || dates.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div>
              <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b align-bottom">
                      <th className="py-2 pr-4">Ürün</th>
                      {dates.map(d => (<th key={d} className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">{formatDayShort(d)}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedProducts.map(key => {
                      const name = key.split('#')[0];
                      return (
                        <tr key={key} className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-4 font-medium">{name}</td>
                          {dates.map(d => (
                            <td key={`${key}-${d}`} className="py-2 pr-4">{fmtCell((byProd.get(key)?.[d]) || 0, metric)}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">
                    Showing {startIdx + 1}-{Math.min(startIdx + PRODUCTS_PER_PAGE, products.length)} of {products.length} products
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-2.5 py-1 text-xs border border-gray-200 rounded-md disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setProductDailyPage(p => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span className="text-xs text-gray-600">Sayfa {currentPage} / {totalPages}</span>
                    <button
                      className="px-2.5 py-1 text-xs border border-gray-200 rounded-md disabled:opacity-50"
                      disabled={currentPage >= totalPages}
                      onClick={() => setProductDailyPage(p => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      </>
      )}
    </div>
  );
}


